import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Tooltip from '../common/tooltip/ToolTip';
import NavigationIcons from '../navigation/components/NavigationIcons';
import { useAppSelector } from '../../hooks/storeHooks';
import './GroupStatus.scss';

interface CustomTableProps {
  rawData: any;
  index: number;
  cell: any;
  rowClassName?: string;
  style?: React.CSSProperties;
  href: string;
  customViews?: boolean;
}

const GroupStatusCell: React.FC<CustomTableProps> = ({
  rawData,
  cell,
  rowClassName,
  style,
  href,
  customViews = false,
}) => {
  const navigate = useNavigate();

  // Get asset groups from Redux state to find industryApplicationId (only when in custom views)
  const assets = useAppSelector((state) => state.assetGroups?.assets);

  // Helper function to find industryApplicationId by well name
  const findIndustryApplicationIdByWellName = (wellName: string): number => {
    if (!wellName || !customViews) return 0;

    if (assets) {
      const asset = assets.find((asset: any) => asset.assetName === wellName);
      if (asset?.industryApplicationId) {
        return asset.industryApplicationId;
      }
    }

    return 0; // Default fallback
  };

  const handleAnchorClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      navigate(href);
    },
    [navigate, href],
  );

  const renderCell = (cell: any) => {
    if (cell.column.Header !== 'Assetguid') {
      const cellIndex = cell.index;
      const isLastCell =
        cell.row.getVisibleCells().length - 2 === cellIndex || cell.row.getVisibleCells().length - 3 === cellIndex;

      if (cell.column.Header !== 'Assetguid') {
        let cellValue = cell.getValue();
        const wellCell = cell.row.getVisibleCells().find((cell: any) => cell.column.columnDef.header === 'Well');
        const wellName = wellCell ? wellCell.getValue() : undefined;
        const rowRawData = rawData?.find?.((row: any) => row?.Well?.value === wellName) ?? {};

        const cellDetails = rowRawData?.[cell.column.id] ?? {};

        switch (cell.column.id.toLowerCase()) {
          case 'well':
            if (customViews) {
              const wellName = cell.getValue();
              const industryApplicationId = findIndustryApplicationIdByWellName(wellName);
              const asset = { industryApplicationId };

              cellValue = (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', ...style }} className={rowClassName}>
                  <NavigationIcons asset={asset} isActive={false} />
                  <a
                    key={cellIndex}
                    href='#'
                    className='table-cell-link'
                    onClick={handleAnchorClick}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      width: `calc(${cell.column.getSize()}px - 35px)`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span style={{ color: '#60bfda', wordBreak: 'break-all' }} title={wellName}>
                      {wellName}
                    </span>
                  </a>
                </div>
              );
            } else {
              cellValue = (
                <a key={cellIndex} href='#' className='table-row-link' onClick={handleAnchorClick}>
                  {cell.getValue()}
                </a>
              );
            }
            break;
          case '7':
            cellValue = (
              <div key={cellIndex} className='alarm-badges'>
                {cell.getValue()?.map((alarm: string, alarmIndex: number) => (
                  <div key={alarmIndex} className='badge'>
                    <span className='badge-text'>{alarm}</span>
                  </div>
                ))}
              </div>
            );
            break;
          default:
            break;
        }

        if (cellDetails.backColor && cell.getValue()) {
          cellValue = (
            <div key={cellIndex} className='alarm-badges' style={{ display: 'flex' }}>
              <div
                className='badge'
                style={{
                  backgroundColor: 'transparent',
                  color: cellDetails.backColor,
                  border: `1px solid ${cellDetails.backColor}`,
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                }}
              >
                {cell.getValue()}
              </div>
            </div>
          );
        }

        if (!cellDetails?.showToolTip) {
          return <div key={cell.id}>{cellValue}</div>;
        } else {
          const tooltipDirection = isLastCell ? 'Left' : 'right';
          const toolTipText = cellDetails.value;

          return (
            <div key={cell.id} style={{ alignItems: 'center', display: 'flex' }}>
              <Tooltip content={toolTipText} direction={tooltipDirection} isGroupStatus={true}>
                {cellValue}
              </Tooltip>
            </div>
          );
        }
      }
    }
    return null;
  };

  return <>{renderCell(cell)}</>;
};

export default React.memo(GroupStatusCell);
